import { useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { Button as AntButton, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { PaperClipOutlined, SendOutlined } from '@ant-design/icons';
import { Textarea } from '@shared/ui';
import { ALLOWED_CONTENT_TYPES } from '@entities/media';
import { MediaPicker } from '@features/media-picker';

interface ComposerProps {
  onSend: (body: string, attachments: string[]) => Promise<void> | void;
  disabled?: boolean;
}

const ATTACH_TYPES = [...ALLOWED_CONTENT_TYPES.image, ...ALLOWED_CONTENT_TYPES.document];

export function Composer({ onSend, disabled }: ComposerProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<string[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const reset = () => {
    setBody('');
    setAttachments([]);
    setShowPicker(false);
  };

  const submit = async () => {
    const text = body.trim();
    if (!text && attachments.length === 0) return;
    await onSend(text, attachments);
    reset();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  const handleDrop = (e: DragEvent<HTMLFormElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) setShowPicker(true);
  };

  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        boxShadow: dragOver ? `inset 0 0 0 2px ${token.colorPrimary}40` : 'none',
        transition: 'box-shadow 150ms ease',
      }}
    >
      {showPicker ? (
        <MediaPicker
          scope="CHAT_ATTACHMENT"
          accept={ATTACH_TYPES}
          multiple
          value={attachments}
          onChange={setAttachments}
        />
      ) : null}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <AntButton
          type="text"
          shape="circle"
          icon={<PaperClipOutlined />}
          onClick={() => setShowPicker((v) => !v)}
          aria-label={t('chats.attach')}
        />
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder={t('chats.messagePlaceholder')}
          disabled={disabled}
        />
        <AntButton
          type="primary"
          icon={<SendOutlined />}
          htmlType="submit"
          disabled={disabled || (!body.trim() && attachments.length === 0)}
        >
          {t('chats.send')}
        </AntButton>
      </div>
    </form>
  );
}
